import { Component, OnDestroy, OnInit } from '@angular/core';
import { fuseAnimations } from '@fuse/animations';
import { UserService } from 'app/shared/services/user.service';
import { User } from 'app/shared/models/user';
import { Subject, Subscription } from 'rxjs';
import { CheckListItem } from 'app/components/check-list/check-list.component';
import { debounceTime, take } from 'rxjs/operators';
import { environment } from 'environments/environment';

@Component({
  selector: 'app-user-selection-widget',
  templateUrl: './user-selection-widget.component.html',
  styleUrls: ['./user-selection-widget.component.scss'],
  animations: fuseAnimations,
})
export class UserSelectionWidgetComponent implements OnInit, OnDestroy {
  users: User[] = [];
  userCheckList: CheckListItem[];
  userAggregateCheckList: CheckListItem[];
  _userAggregate: CheckListItem;

  debouncer: Subject<any> = new Subject();
  activeUsers: Subscription;

  constructor(private userService: UserService) {

    // debouncer used to debounce user changes - this should mean that the selectedUserChanged isn't repeatedly triggered as changes are made
    this.debouncer
      .pipe(debounceTime(1000))
      .subscribe((_) => {
        this.selectedUserChanged();
      });
  }

  ngOnInit() {
    const currentUserId = this.userService.getCurrentUserId();

    const me: User = <User>this.userService.getCurrentUser();
    let orgId: number | undefined;
    let includeAdmin: boolean | undefined;
    
    if (me.organisationId) {
      orgId = me.organisationId;
      includeAdmin = !(me && me.isAdmin === false && me.organisationId > 0);
    }

    this.userService.filteringByUser.next(true);
    if (environment.limitAccessToSubjectsByOrg === true) {
      this.activeUsers = this.userService.getActiveUsersForOrganisation(orgId, includeAdmin).subscribe((users) => {
        this.users = users;
        const selectedUsers = this.userService.selectedUsers;
        if (selectedUsers.length === 0 && currentUserId) {
          selectedUsers.push(currentUserId);
        }

        this.userCheckList = this.users.map((user) => new CheckListItem(user.id, user.fullName, (selectedUsers.indexOf(user.id) !== -1)));
      });
    }
    else {
      this.activeUsers = this.userService.getActiveUsers().subscribe((users) => {
        this.users = users;
        const selectedUsers = this.userService.selectedUsers;
        if (selectedUsers.length === 0 && currentUserId) {
          selectedUsers.push(currentUserId);
        }
        this.userCheckList = this.users.map((user) => new CheckListItem(user.id, user.fullName, (selectedUsers.indexOf(user.id) !== -1)));
      });
    }

    this._userAggregate = this.userService.userAggregate;
    this.userAggregateCheckList = [
      new CheckListItem(1, 'Me', false),
      new CheckListItem(2, 'People I manage', false),
      new CheckListItem(3, 'Everyone', false)];
  }

  ngOnDestroy() {
    this.userService.filteringByUser.next(false);
    // stash current useraggregate selection, just in case we need it
    this.userService.userAggregate = this._userAggregate;

    this.activeUsers?.unsubscribe();
    this.debouncer?.unsubscribe();
  }

  userAggregateChanged(item: CheckListItem) {
    this._userAggregate = item;
    switch (item.id) {
      case 1:
        this.deselectUsers();
        this.selectCurrentUser();
        break;
      case 2:
        this.deselectUsers();
        this.selectUsersWhoAreManagedByCurrentUser();
        break;
      case 3:
        this.selectAllUsers();
        break;
    }
    this.debouncer.next(item);
  }

  usersChanged(item: CheckListItem) {
    this.userAggregateCheckList.forEach((i) => i.checked = false);
    this.debouncer.next(item);
  }

  selectedUserChanged() {
    const selectedItems = this.userCheckList.filter((item) => item.checked === true).map((item) => item.id.toString());
    const users = this.users.filter((user) => selectedItems.indexOf(user.id.toString()) !== -1).map((user) => user.id);
    this.userService.selectedUsersChanged(users);
  }

  selectAllUsers() {
    this.userCheckList.forEach((item) => {
      item.checked = true;
    });
  }

  deselectUsers() {
    this.userCheckList.forEach((item) => {
      item.checked = false;
    });
  }

  selectCurrentUser() {
    const currentUserId = this.userService.getCurrentUserId();
    if (currentUserId) {
      const item = this.userCheckList.find((u) => u.id.toString() === currentUserId.toString());
      if (item) {
        item.checked = true;
      } 
    }
  }

  selectUsersWhoAreManagedByCurrentUser() {
    this.userService.getActiveUsers().pipe(take(1)).subscribe((u: User[]) => {
      u = u || [];
      const users = u.filter((user) => user.managerId === this.userService.getCurrentUserId());
      users.map((user) => {
        const checkListItem = this.userCheckList.find((item) => item.id === user.id);
        if (checkListItem) {
          checkListItem.checked = true;
        }
      });
    });
  }
}
